<template>
  <Modal
    :show="show"
    title="Search"
    icon-type="none"
    hide-header
    :show-close-btn="false"
    max-width-class="sm:max-w-4xl"
    background-colour-class="bg-fp-gray-1"
    panel-padding-class="p-0"
    align="top"
    :initial-focus="searchInputRef"
    @close="emit('close')"
  >
    <div class="flex flex-col overflow-hidden rounded-lg">
      <div
        class="flex items-center gap-2 bg-white p-2"
        :class="hasSearchBody ? 'border-b border-fp-gray-2' : ''"
      >
        <SearchCircleIcon
          class="size-6 shrink-0 text-fp-gray-2"
          aria-hidden="true"
        />

        <label class="sr-only" :for="inputId">Search tools and records</label>
        <input
          :id="inputId"
          ref="searchInputRef"
          type="search"
          :value="query"
          placeholder="Search for anything..."
          autocomplete="off"
          class="global-search-input min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-fp-gray-6 placeholder:text-fp-gray-6/40 focus:ring-0"
          :aria-activedescendant="highlightedResultId"
          @input="onQueryInput"
        />
      </div>

      <div v-if="hasSearchBody" class="relative">
        <div
          v-if="filterMenuOpen"
          class="absolute inset-0 z-10 bg-fp-gray-1/60 backdrop-blur-xs"
          data-testid="global-search-filter-menu-backdrop"
          aria-hidden="true"
        />
        <GlobalSearchFilters
          v-model="selectedFilter"
          :record-counts="recordCountByType"
          :tool-count="toolResults.length"
          :active="show"
          @update:menu-open="filterMenuOpen = $event"
        />
        <div class="relative">
          <div
            ref="resultsScrollRef"
            :class="[
              'max-h-96 overflow-y-auto px-4 pb-4',
              filterMenuOpen ? 'min-h-96' : '',
            ]"
            data-testid="global-search-results"
            @scroll="updateResultsFade"
          >
            <section v-if="showToolsList" aria-label="Tools">
              <ul>
                <li
                  v-for="(item, index) in visibleToolResults"
                  :key="item.to"
                  class="p-1"
                  :class="index > 0 ? 'border-t border-fp-gray-2' : ''"
                >
                  <NuxtLink
                    :id="resultDomId(index)"
                    :to="item.to"
                    :aria-label="`${item.name} Tool`"
                    :aria-selected="highlightedIndex === index"
                    :class="[
                      'flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-fp-gray-2/50',
                      highlightedIndex === index ? 'bg-fp-gray-2/50' : '',
                    ]"
                    @click="onNavigate"
                  >
                    <span class="min-w-0 truncate text-sm-med text-fp-gray-6">
                      {{ item.name }}
                    </span>
                    <span
                      class="inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-fp-gray-2/50 px-2 py-1 text-xs-semibold text-fp-gray-5"
                    >
                      <component
                        :is="GLOBAL_SEARCH_TOOLS_ICON.component"
                        :class="[
                          'size-3.5 shrink-0',
                          GLOBAL_SEARCH_TOOLS_ICON.colorClass,
                        ]"
                        aria-hidden="true"
                      />
                      Tool
                    </span>
                  </NuxtLink>
                </li>
              </ul>
            </section>

            <section v-if="showRecordsPrompt">
              <p class="text-sm text-fp-gray-4">
                Type at least 2 characters to search records
              </p>
            </section>

            <section
              v-else-if="showRecordsLoading"
              class="flex flex-col items-center pb-8 pt-4"
            >
              <p class="text-sm text-fp-gray-4">Searching records…</p>
            </section>

            <section v-else-if="showRecordsList" aria-label="Records">
              <ul>
                <li
                  v-for="(hit, index) in visibleRecordResults"
                  :key="`${hit.recordType}-${hit.recordId}`"
                  class="p-1"
                  :class="
                    index > 0 || showToolsList
                      ? 'border-t border-fp-gray-2'
                      : ''
                  "
                >
                  <NuxtLink
                    :id="resultDomId(recordResultIndex(index))"
                    :to="recordHref(hit)"
                    :aria-selected="
                      highlightedIndex === recordResultIndex(index)
                    "
                    :class="[
                      'flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-fp-gray-2/50',
                      highlightedIndex === recordResultIndex(index)
                        ? 'bg-fp-gray-2/50'
                        : '',
                    ]"
                    @click="onNavigate"
                  >
                    <span
                      class="flex min-w-0 flex-col items-start justify-center"
                    >
                      <span class="truncate text-sm-med text-fp-gray-6">
                        {{ hit.label }}
                      </span>
                      <span
                        v-if="extraDataLabel(hit)"
                        class="truncate text-xs text-fp-gray-5"
                      >
                        {{ extraDataLabel(hit) }}
                      </span>
                    </span>
                    <span
                      class="inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-fp-gray-2/50 px-2 py-1 text-xs-semibold text-fp-gray-5"
                    >
                      <component
                        :is="
                          globalSearchRecordTypeIcon(hit.recordType)?.component
                        "
                        v-if="globalSearchRecordTypeIcon(hit.recordType)"
                        :class="[
                          'size-3.5 shrink-0',
                          globalSearchRecordTypeIcon(hit.recordType)
                            ?.colorClass,
                        ]"
                        aria-hidden="true"
                      />
                      {{ globalSearchRecordTypeLabel(hit.recordType) }}
                    </span>
                  </NuxtLink>
                </li>
              </ul>
            </section>

            <div
              v-if="showEmptyState"
              class="flex flex-col items-center gap-2 px-4 py-8"
            >
              <img
                src="/images/sidebar/no-results-found-guy.png"
                alt=""
                class="h-[120px] w-[91px] object-contain"
                aria-hidden="true"
              />
              <p class="text-sm text-fp-gray-4">No Results</p>
              <button
                type="button"
                class="link-secondary px-1 text-sm"
                @click="clearQuery"
              >
                Clear
              </button>
            </div>
          </div>

          <div
            v-show="showResultsFade"
            data-testid="global-search-results-fade"
            class="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-fp-gray-1 to-transparent"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import Modal from '~/components/common/Modal.vue'
import SearchCircleIcon from '~/../member-webapp-shared/components/icons/streamline/SearchCircleIcon.vue'
import GlobalSearchFilters, {
  type SearchFilter,
} from '~/components/navigation/GlobalSearchFilters.vue'
import { useAdminNavigationMenu } from '~/composables/useAdminNavigationMenu'
import {
  GLOBAL_SEARCH_TOOLS_ICON,
  globalSearchHitHref,
  globalSearchRecordTypeIcon,
  globalSearchRecordTypeLabel,
} from '~/utils/globalSearchLinks'
import type { GlobalSearchHit } from '~~/models/graphql/generated/types'
import { GlobalSearchRecordTypeEnum } from '~~/models/graphql/generated/types'

const props = defineProps<{
  show: boolean
  query: string
}>()

const emit = defineEmits<{
  (event: 'close' | 'navigate'): void
  (event: 'update:query', value: string): void
}>()

const { menu } = useAdminNavigationMenu()
const bffClient = useBffClient()

const inputId = useId()
const searchInputRef = ref<HTMLInputElement | null>(null)
const resultsScrollRef = ref<HTMLElement | null>(null)
const recordsLoading = ref(false)
const recordResults = ref<GlobalSearchHit[]>([])
const selectedFilter = ref<SearchFilter>('top')
const filterMenuOpen = ref(false)
const showResultsFade = ref(false)
const highlightedIndex = ref(-1)

const toolResults = computed(() => {
  const q = props.query.trim().toLowerCase()
  if (!q) return []
  return menu.value.filter((item) => item.name.toLowerCase().includes(q))
})

const recordCountByType = computed(() => {
  const counts = Object.fromEntries(
    Object.values(GlobalSearchRecordTypeEnum).map((type) => [type, 0])
  ) as Record<GlobalSearchRecordTypeEnum, number>
  for (const hit of recordResults.value) {
    counts[hit.recordType] += 1
  }
  return counts
})

const showToolsSection = computed(
  () => selectedFilter.value === 'top' || selectedFilter.value === 'tools'
)

const showRecordsSection = computed(() => selectedFilter.value !== 'tools')

const TOP_RESULTS_LIMIT = 10

const visibleToolResults = computed(() => {
  if (selectedFilter.value === 'top') {
    return toolResults.value.slice(0, TOP_RESULTS_LIMIT)
  }
  return toolResults.value
})

const visibleRecordResults = computed(() => {
  if (selectedFilter.value === 'tools') return []
  if (selectedFilter.value === 'top') {
    const remaining = TOP_RESULTS_LIMIT - visibleToolResults.value.length
    return recordResults.value.slice(0, remaining)
  }
  return recordResults.value.filter(
    (hit) => hit.recordType === selectedFilter.value
  )
})

const showToolsList = computed(
  () => showToolsSection.value && visibleToolResults.value.length > 0
)

const hasSearchBody = computed(() => props.query.trim().length > 0)

const showRecordsPrompt = computed(
  () =>
    showRecordsSection.value &&
    props.query.trim().length > 0 &&
    props.query.trim().length < 2
)

const showRecordsLoading = computed(
  () =>
    showRecordsSection.value &&
    recordsLoading.value &&
    props.query.trim().length >= 2
)

const showRecordsList = computed(
  () => showRecordsSection.value && visibleRecordResults.value.length > 0
)

const keyboardResultCount = computed(() => {
  const toolCount = showToolsList.value ? visibleToolResults.value.length : 0
  const recordCount = showRecordsList.value
    ? visibleRecordResults.value.length
    : 0
  return toolCount + recordCount
})

const highlightedResultId = computed(() => {
  if (highlightedIndex.value < 0) return undefined
  return resultDomId(highlightedIndex.value)
})

const showEmptyState = computed(
  () =>
    props.query.trim().length >= 2 &&
    !recordsLoading.value &&
    !showToolsList.value &&
    !showRecordsList.value
)

watch(
  () => props.show,
  async (show) => {
    if (!show) {
      selectedFilter.value = 'top'
      filterMenuOpen.value = false
      showResultsFade.value = false
      highlightedIndex.value = -1
      window.removeEventListener('keydown', onSearchKeydown, true)
      return
    }
    window.addEventListener('keydown', onSearchKeydown, true)
    await nextTick()
    searchInputRef.value?.focus()
    updateResultsFade()
  }
)

watch(
  keyboardResultCount,
  (count) => {
    highlightedIndex.value = count > 0 ? 0 : -1
    void nextTick(() => scrollHighlightedIntoView())
  },
  { immediate: true }
)

watch(
  [visibleToolResults, visibleRecordResults, recordsLoading, selectedFilter],
  async () => {
    await nextTick()
    updateResultsFade()
  }
)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let searchRequestId = 0

watch(
  () => [props.show, props.query] as const,
  ([show, query]) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    if (!show) {
      searchRequestId += 1
      recordResults.value = []
      recordsLoading.value = false
      return
    }

    const trimmed = query.trim()
    if (trimmed.length < 2) {
      searchRequestId += 1
      recordResults.value = []
      recordsLoading.value = false
      return
    }

    recordsLoading.value = true
    const requestId = ++searchRequestId
    debounceTimer = setTimeout(async () => {
      try {
        const results = await bffClient.globalSearch(trimmed)
        if (requestId !== searchRequestId || !props.show) return
        recordResults.value = results
      } catch {
        if (requestId !== searchRequestId || !props.show) return
        recordResults.value = []
      } finally {
        if (requestId === searchRequestId && props.show) {
          recordsLoading.value = false
        }
      }
    }, 300)
  },
  { immediate: true }
)

function updateResultsFade() {
  const scroller = resultsScrollRef.value
  if (!scroller) {
    showResultsFade.value = false
    return
  }

  const remaining =
    scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight
  showResultsFade.value = remaining > 1
}

function onQueryInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:query', target.value)
}

function clearQuery() {
  emit('update:query', '')
}

function recordHref(hit: GlobalSearchHit) {
  return globalSearchHitHref(hit)
}

function extraDataLabel(hit: GlobalSearchHit) {
  const parts = hit.extraData?.filter((part) => part.trim()) ?? []
  return parts.length > 0 ? parts.join(' • ') : null
}

function recordResultIndex(index: number) {
  return showToolsList.value ? visibleToolResults.value.length + index : index
}

function resultDomId(index: number) {
  return `${inputId}-result-${index}`
}

function onSearchKeydown(event: KeyboardEvent) {
  if (!props.show || filterMenuOpen.value) return

  const count = keyboardResultCount.value
  if (count === 0) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    highlightedIndex.value = (highlightedIndex.value + 1 + count) % count
    void nextTick(() => scrollHighlightedIntoView())
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    const current = highlightedIndex.value < 0 ? 0 : highlightedIndex.value
    highlightedIndex.value = (current - 1 + count) % count
    void nextTick(() => scrollHighlightedIntoView())
    return
  }

  if (event.key === 'Enter') {
    if (highlightedIndex.value < 0) return
    event.preventDefault()
    activateHighlighted()
  }
}

function scrollHighlightedIntoView() {
  if (highlightedIndex.value < 0) return
  const result = document.getElementById(resultDomId(highlightedIndex.value))
  result?.scrollIntoView?.({ block: 'nearest' })
}

function activateHighlighted() {
  const result = document.getElementById(resultDomId(highlightedIndex.value))
  if (result instanceof HTMLElement) {
    result.click()
  }
}

function onNavigate() {
  emit('navigate')
}

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onSearchKeydown, true)
})
</script>

<style scoped>
.global-search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}
</style>
